// =====================================================
// EDITOR DE POLÍGONOS - ESTABLECIMIENTO
// src/pages/Agro/Establecimiento/EditorPoligonos.js
// =====================================================

import React, { useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import {
  Map,
  TileLayer,
  FeatureGroup,
  Polygon,
  Popup,
  Marker,
} from "react-leaflet"
import "leaflet/dist/leaflet.css"
import "leaflet-draw/dist/leaflet.draw.css"
import L from "leaflet"
import "leaflet-draw"

import {
  getPoligonosByEstablecimiento,
  savePoligono,
  deletePoligono,
  updatePoligono,
} from "api/agro"
import toastHandler from "toastHandler"
import { formatearNumero } from "helpers/formatters"
import {
  colorUso,
  estiloLote,
  LEYENDA_POLIGONO_APROXIMADO,
  labelUsoLote,
  TENENCIAS_LOTE,
  USO_LOTE_DEFAULT,
} from "constants/usosLote"
import { useUsosLote } from "hooks/useUsosLote"
import PatronesLote, { MuestraLote } from "components/Mapa/PatronesLote"
import LeyendaLotes from "components/Mapa/LeyendaLotes"

// Fix para iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

const EditorPoligonos = ({
  idEstablecimiento,
  establecimiento,
  readonly = false,
  onLoteActualizado,
}) => {
  // BDSJEA-409: catálogo de usos del backend (misma fuente que el combo de destino del establecimiento)
  const { usos: USOS_LOTE, error: errorUsos } = useUsosLote()
  const [loading, setLoading] = useState(true)
  const [poligonos, setPoligonos] = useState([])
  const [modalNuevo, setModalNuevo] = useState(false)
  const [nuevoPoligono, setNuevoPoligono] = useState(null)
  const [eliminandoId, setEliminandoId] = useState(null)
  const [formData, setFormData] = useState({
    nombreLote: "",
    usoActual: USO_LOTE_DEFAULT,
    superficieHa: "",
    tipoTenencia: "",
  })
  // BDSJEA-462/463: edición de superficie, destino y tenencia de un lote (el nombre es solo lectura)
  const [loteEnEdicion, setLoteEnEdicion] = useState(null)
  const [formEdicion, setFormEdicion] = useState({
    superficieHa: "",
    usoActual: "",
    tipoTenencia: "",
  })
  const [guardandoEdicion, setGuardandoEdicion] = useState(false)
  const mapRef = useRef()
  const featureGroupRef = useRef()

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)

  useEffect(() => {
    if (idEstablecimiento) {
      loadPoligonos()
    }
  }, [idEstablecimiento])

  const loadPoligonos = async () => {
    setLoading(true)
    try {
      const data = await getPoligonosByEstablecimiento(idEstablecimiento)
      setPoligonos(data)
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  // Centrar mapa en el establecimiento o en Argentina
  const getCentro = () => {
    if (establecimiento?.latitud && establecimiento?.longitud) {
      return [establecimiento.latitud, establecimiento.longitud]
    }
    return [-34.6037, -58.3816] // Buenos Aires por defecto
  }

  const getZoom = () => {
    if (establecimiento?.latitud && establecimiento?.longitud) {
      return 14
    }
    return 5
  }

  // Inicializar controles de dibujo (Leaflet Draw) para react-leaflet v2
  const initDrawControls = () => {
    if (!mapRef.current || !featureGroupRef.current) return

    const map = mapRef.current.leafletElement
    const featureGroup = featureGroupRef.current.leafletElement

    // Crear control de dibujo
    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        polygon: {
          allowIntersection: false,
          drawError: {
            color: "#e74c3c",
            message: "Los bordes no pueden cruzarse",
          },
          shapeOptions: {
            color: "#3388ff",
            fillOpacity: 0.3,
          },
        },
        polyline: false,
        circle: false,
        rectangle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: featureGroup,
        edit: false,
        remove: false,
      },
    })

    map.addControl(drawControl)

    // Evento cuando se crea un polígono
    map.on(L.Draw.Event.CREATED, e => {
      const layer = e.layer
      featureGroup.addLayer(layer)

      // Calcular superficie
      const latlngs = layer.getLatLngs()[0]
      let area = 0
      if (L.GeometryUtil && L.GeometryUtil.geodesicArea) {
        area = L.GeometryUtil.geodesicArea(latlngs)
      } else {
        // Fallback: cálculo aproximado
        area = L.GeometryUtil ? L.GeometryUtil.geodesicArea(latlngs) : 0
      }
      const hectares = Math.round((area / 10000) * 100) / 100

      // Convertir a GeoJSON
      const geojson = JSON.stringify(layer.toGeoJSON())

      setNuevoPoligono({
        layer,
        geojson,
        superficieCalculada: hectares,
      })

      setFormData({
        nombreLote: `Lote ${poligonos.length + 1}`,
        usoActual: USO_LOTE_DEFAULT,
        superficieHa: hectares.toString(),
        tipoTenencia: "",
      })

      setModalNuevo(true)
    })
  }

  // Efecto para inicializar draw controls después de que el mapa esté listo
  useEffect(() => {
    if (!readonly && mapRef.current && featureGroupRef.current) {
      // Pequeño delay para asegurar que el mapa esté completamente inicializado
      const timer = setTimeout(() => {
        initDrawControls()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [loading, readonly])

  const handleGuardarPoligono = async () => {
    if (!nuevoPoligono) return

    try {
      const data = {
        idEstablecimiento,
        nombre: formData.nombreLote,
        usoActual: formData.usoActual,
        superficieHa: parseFloat(formData.superficieHa),
        // BDSJEA-463: un lote tiene UNA tenencia (propio | arrendado), obligatoria en el alta manual
        tipoTenencia: formData.tipoTenencia,
        geojson: nuevoPoligono.geojson,
      }

      await savePoligono(data)
      setSuccess("Polígono guardado correctamente")

      setModalNuevo(false)
      setNuevoPoligono(null)
      setFormData({
        nombreLote: "",
        usoActual: USO_LOTE_DEFAULT,
        superficieHa: "",
        tipoTenencia: "",
      })

      loadPoligonos()
    } catch (error) {
      setError(error)
    }
  }

  const handleEliminarPoligono = async idPoligono => {
    if (!window.confirm("¿Está seguro de eliminar este polígono?")) return

    setEliminandoId(idPoligono)
    try {
      await deletePoligono(idPoligono)
      setSuccess("Polígono eliminado")
      loadPoligonos()
    } catch (error) {
      setError(error)
    } finally {
      setEliminandoId(null)
    }
  }

  const abrirEdicion = pol => {
    setLoteEnEdicion(pol)
    setFormEdicion({
      superficieHa: pol.superficieHa ?? "",
      usoActual: pol.usoActual || "",
      tipoTenencia: pol.tipoTenencia || "",
    })
  }

  const cerrarEdicion = () => {
    if (guardandoEdicion) return
    setLoteEnEdicion(null)
  }

  // Solo se manda (y se valida) lo que el usuario tocó: un lote importado de 0 ha sigue siendo editable en su destino.
  const cambiosEdicion = () => {
    if (!loteEnEdicion) return {}
    const cambios = {}
    const original = loteEnEdicion.superficieHa
    const tocada =
      formEdicion.superficieHa === ""
        ? original != null
        : Number(formEdicion.superficieHa) !== Number(original ?? NaN)
    if (tocada) cambios.superficieHa = formEdicion.superficieHa
    if (formEdicion.usoActual !== (loteEnEdicion.usoActual || "")) {
      cambios.usoActual = formEdicion.usoActual
    }
    // BDSJEA-463: la tenencia solo se manda si el usuario la cambió (null = sin dato: no se puede volver a "sin dato")
    if (formEdicion.tipoTenencia !== (loteEnEdicion.tipoTenencia || "")) {
      cambios.tipoTenencia = formEdicion.tipoTenencia
    }
    return cambios
  }

  const edicionValida = () => {
    const cambios = cambiosEdicion()
    if (Object.keys(cambios).length === 0) return false
    if ("superficieHa" in cambios) {
      const n = Number(cambios.superficieHa)
      if (cambios.superficieHa === "" || isNaN(n) || n <= 0) return false
    }
    if ("usoActual" in cambios && !cambios.usoActual) return false
    if ("tipoTenencia" in cambios && !cambios.tipoTenencia) return false
    return true
  }

  const handleGuardarEdicion = async () => {
    if (!loteEnEdicion || !edicionValida()) return
    const cambios = cambiosEdicion()
    const payload = {}
    if ("superficieHa" in cambios)
      payload.superficieHa = parseFloat(cambios.superficieHa)
    if ("usoActual" in cambios) payload.usoActual = cambios.usoActual
    if ("tipoTenencia" in cambios)
      payload.codigoTipoTenencia = cambios.tipoTenencia

    setGuardandoEdicion(true)
    try {
      await updatePoligono(loteEnEdicion.id, payload)
      setSuccess("Lote actualizado")
      setLoteEnEdicion(null)
      await loadPoligonos()
      if (onLoteActualizado) onLoteActualizado()
    } catch (error) {
      // el mensaje descriptivo del backend lo resuelve toastHandler (error_helpers)
      setError(error)
    } finally {
      setGuardandoEdicion(false)
    }
  }

  const superficieTotal = poligonos.reduce(
    (sum, p) => sum + (p.superficieHa || 0),
    0,
  )

  return (
    <>
      <Row>
        <Col md={8}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <i className="mdi mdi-map me-2" />
                  Mapa del Establecimiento
                </h5>
                {!readonly && (
                  <Alert color="info" className="mb-0 py-2 px-3">
                    <small>
                      <i className="mdi mdi-information me-1" />
                      Use las herramientas del mapa para dibujar los lotes
                    </small>
                  </Alert>
                )}
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : (
                <div style={{ height: "600px", border: "1px solid #ddd" }}>
                  {/* BDSJEA-463: tramas SVG (punteado = propio, rayado = arrendado) */}
                  <PatronesLote />
                  <Map
                    center={getCentro()}
                    zoom={getZoom()}
                    style={{ height: "100%", width: "100%" }}
                    ref={mapRef}
                    whenReady={() => {
                      // Inicializar herramientas de dibujo cuando el mapa esté listo
                      if (
                        !readonly &&
                        mapRef.current &&
                        featureGroupRef.current
                      ) {
                        initDrawControls()
                      }
                    }}
                  >
                    {/* Capa satelital */}
                    <TileLayer
                      attribution='Imagery &copy; <a href="https://www.esri.com/">Esri</a>'
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    />

                    {/* Referencias (limites, nombres) sobre la capa satelital */}
                    <TileLayer
                      attribution='Imagery &copy; <a href="https://www.esri.com/">Esri</a>'
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    />

                    {/* Marcador del establecimiento */}
                    {establecimiento?.latitud && establecimiento?.longitud && (
                      <Marker
                        position={[
                          establecimiento.latitud,
                          establecimiento.longitud,
                        ]}
                      >
                        {/* BDSJEA-134: se quita el RENSPA del popup. */}
                        <Popup>
                          <strong>{establecimiento.nombre}</strong>
                        </Popup>
                      </Marker>
                    )}

                    {/* Polígonos existentes */}
                    {poligonos.map(poligono => {
                      if (!poligono.geojson) return null

                      try {
                        const geojson = JSON.parse(poligono.geojson)
                        let coords = []
                        if (geojson.type === "Feature" && geojson.geometry) {
                          coords = geojson.geometry.coordinates[0]
                        } else if (geojson.type === "Polygon") {
                          coords = geojson.coordinates[0]
                        }
                        if (!coords || !coords.length) return null

                        const positions = coords.map(coord => [
                          coord[1],
                          coord[0],
                        ])

                        // BDSJEA-463: color por destino + trama por tenencia
                        const estilo = estiloLote(poligono)

                        return (
                          <Polygon
                            key={poligono.id}
                            positions={positions}
                            color={estilo.color}
                            fillColor={estilo.fillColor}
                            fillOpacity={estilo.fillOpacity}
                            weight={estilo.weight}
                            dashArray={estilo.dashArray}
                          >
                            <Popup>
                              <strong>{poligono.nombreLote}</strong>
                              <br />
                              Superficie:{" "}
                              {formatearNumero(poligono.superficieHa, 2)} ha
                              <br />
                              Uso: {labelUsoLote(poligono.usoActual, USOS_LOTE)}
                              <br />
                              Tenencia: {estilo.etiquetaTenencia}
                              {estilo.aproximado && (
                                <>
                                  <br />
                                  <em data-testid="popup-aproximado">
                                    {LEYENDA_POLIGONO_APROXIMADO}
                                  </em>
                                </>
                              )}
                              {poligono.esDefault && (
                                <>
                                  <br />
                                  Lote generado automáticamente
                                </>
                              )}
                              {!readonly && (
                                <Button
                                  size="sm"
                                  color="primary"
                                  outline
                                  className="mt-2 me-1"
                                  title="Editar superficie, destino y tenencia de este lote"
                                  onClick={() => abrirEdicion(poligono)}
                                >
                                  <i className="mdi mdi-pencil" /> Editar
                                </Button>
                              )}
                              {!readonly && (
                                <Button
                                  size="sm"
                                  color="danger"
                                  className="mt-2"
                                  title="Eliminar este polígono"
                                  disabled={eliminandoId === poligono.id}
                                  onClick={() =>
                                    handleEliminarPoligono(poligono.id)
                                  }
                                >
                                  {eliminandoId === poligono.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <>
                                      <i className="mdi mdi-delete" /> Eliminar
                                    </>
                                  )}
                                </Button>
                              )}
                            </Popup>
                          </Polygon>
                        )
                      } catch (e) {
                        console.error("Error parseando GeoJSON:", e)
                        return null
                      }
                    })}

                    {/* FeatureGroup para dibujo */}
                    <FeatureGroup ref={featureGroupRef} />
                  </Map>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md={4}>
          <Card>
            <CardBody>
              <h5 className="mb-3">
                <i className="mdi mdi-format-list-bulleted me-2" />
                Lotes Registrados
              </h5>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span>
                    <strong>Superficie Total:</strong>
                  </span>
                  <Badge color="primary" className="px-3">
                    {formatearNumero(superficieTotal, 2)} ha
                  </Badge>
                </div>
                {establecimiento?.superficieTotalHa && (
                  <small className="text-muted">
                    Superficie declarada: {establecimiento.superficieTotalHa} ha
                    {Math.abs(
                      superficieTotal - establecimiento.superficieTotalHa,
                    ) > 1 && (
                      <span className="text-warning ms-1">
                        (Diferencia:{" "}
                        {Math.abs(
                          superficieTotal - establecimiento.superficieTotalHa,
                        ).toFixed(2)}{" "}
                        ha)
                      </span>
                    )}
                  </small>
                )}
              </div>

              {loading ? (
                <div className="text-center py-3">
                  <Spinner size="sm" />
                </div>
              ) : poligonos.length === 0 ? (
                <Alert color="info" className="py-2">
                  <small>
                    No hay lotes registrados.
                    {!readonly &&
                      " Use el mapa para dibujar los lotes del establecimiento."}
                  </small>
                </Alert>
              ) : (
                <div className="list-group">
                  {poligonos.map(pol => {
                    const estilo = estiloLote(pol)
                    const sinSuperficie = Number(pol.superficieHa) === 0
                    return (
                      <div
                        key={pol.id}
                        className="list-group-item list-group-item-action"
                        style={{
                          borderLeft: `4px solid ${colorUso(pol.usoActual)}`,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          {/* BDSJEA-463: chip color (destino) + trama (tenencia); cubre los lotes sin geojson */}
                          <div className="me-2 mt-1">
                            <MuestraLote
                              color={estilo.color}
                              patron={estilo.patron}
                              aproximado={estilo.aproximado}
                              titulo={`${labelUsoLote(pol.usoActual, USOS_LOTE)}, tenencia: ${estilo.etiquetaTenencia}${estilo.aproximado ? ", polígono aproximado" : ""}`}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{pol.nombreLote}</h6>
                            <small className="text-muted">
                              <i className="mdi mdi-ruler-square me-1" />
                              {sinSuperficie
                                ? "0 ha"
                                : `${formatearNumero(pol.superficieHa, 2)} ha`}
                            </small>
                            {sinSuperficie && (
                              <small className="text-muted d-block">
                                Sin superficie informada en el origen
                              </small>
                            )}
                            <br />
                            <Badge color="secondary" className="mt-1">
                              {labelUsoLote(pol.usoActual, USOS_LOTE)}
                            </Badge>
                            <Badge color="info" className="mt-1 ms-1">
                              {estilo.etiquetaTenencia}
                            </Badge>
                            {/* BDSJEA-471: un polígono aproximado no es "Definido" por el operador */}
                            {estilo.aproximado ? (
                              <Badge
                                color="warning"
                                className="mt-1 ms-1"
                                title={LEYENDA_POLIGONO_APROXIMADO}
                                data-testid="badge-aproximado"
                              >
                                <i className="mdi mdi-vector-polygon me-1" />
                                Aproximado
                              </Badge>
                            ) : (
                              <Badge
                                color={pol.esDefault ? "secondary" : "success"}
                                className="mt-1 ms-1"
                              >
                                {pol.esDefault ? "Default" : "Definido"}
                              </Badge>
                            )}
                            {!pol.geojson && (
                              <Badge
                                color="light"
                                className="mt-1 ms-1 text-muted"
                              >
                                Sin polígono
                              </Badge>
                            )}
                          </div>
                          {!readonly && (
                            <div className="d-flex gap-1">
                              <Button
                                size="sm"
                                color="primary"
                                outline
                                title="Editar superficie, destino y tenencia de este lote"
                                aria-label={`Editar ${pol.nombreLote}`}
                                onClick={() => abrirEdicion(pol)}
                              >
                                <i className="mdi mdi-pencil" />
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                title="Eliminar este lote"
                                disabled={eliminandoId === pol.id}
                                onClick={() => handleEliminarPoligono(pol.id)}
                              >
                                {eliminandoId === pol.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <i className="mdi mdi-delete" />
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* BDSJEA-463: leyenda de dos ejes (color = destino, trama = tenencia) */}
              <LeyendaLotes
                usos={USOS_LOTE}
                errorUsos={errorUsos}
                hayAproximados={poligonos.some(
                  pol => pol.aproximado === true && !pol.esDefault,
                )}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal para nuevo polígono */}
      <Modal isOpen={modalNuevo} toggle={() => setModalNuevo(false)}>
        <ModalHeader toggle={() => setModalNuevo(false)}>
          Guardar Nuevo Lote
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="nuevo-lote-nombre">Nombre del Lote *</Label>
            <Input
              id="nuevo-lote-nombre"
              type="text"
              value={formData.nombreLote}
              onChange={e =>
                setFormData({ ...formData, nombreLote: e.target.value })
              }
              placeholder="Ej: Lote 1, Potrero Norte"
            />
          </FormGroup>

          <FormGroup>
            <Label for="nuevo-lote-uso">Uso Actual *</Label>
            <Input
              id="nuevo-lote-uso"
              type="select"
              value={formData.usoActual}
              onChange={e =>
                setFormData({ ...formData, usoActual: e.target.value })
              }
            >
              {USOS_LOTE.map(uso => (
                <option key={uso.value} value={uso.value}>
                  {uso.label}
                </option>
              ))}
            </Input>
            {errorUsos && (
              <div className="text-danger small">
                No se pudieron cargar los destinos: {errorUsos}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="nuevo-lote-tenencia">Tenencia *</Label>
            <Input
              id="nuevo-lote-tenencia"
              type="select"
              value={formData.tipoTenencia}
              onChange={e =>
                setFormData({ ...formData, tipoTenencia: e.target.value })
              }
            >
              <option value="">Seleccione la tenencia...</option>
              {TENENCIAS_LOTE.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              Un lote tiene una sola tenencia: propio o arrendado.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="nuevo-lote-superficie">Superficie (hectáreas) *</Label>
            <Input
              id="nuevo-lote-superficie"
              type="number"
              step="0.01"
              value={formData.superficieHa}
              onChange={e =>
                setFormData({ ...formData, superficieHa: e.target.value })
              }
            />
            {nuevoPoligono && (
              <small className="text-muted">
                Superficie calculada del polígono:{" "}
                {nuevoPoligono.superficieCalculada} ha
              </small>
            )}
          </FormGroup>

          <div className="d-flex justify-content-end gap-2">
            <Button
              color="secondary"
              title="Cancelar y cerrar"
              onClick={() => setModalNuevo(false)}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              title="Guardar el nuevo lote"
              onClick={handleGuardarPoligono}
              disabled={
                !formData.nombreLote ||
                !formData.superficieHa ||
                !formData.tipoTenencia
              }
            >
              <i className="mdi mdi-content-save me-1" />
              Guardar Lote
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* BDSJEA-462/463: modal para editar superficie, destino y tenencia de un lote */}
      <Modal isOpen={!!loteEnEdicion} toggle={cerrarEdicion}>
        <ModalHeader toggle={cerrarEdicion}>Editar lote</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="editar-lote-nombre">Nombre del Lote</Label>
            <Input
              id="editar-lote-nombre"
              type="text"
              value={loteEnEdicion?.nombreLote || ""}
              readOnly
              disabled
            />
            <small className="text-muted">
              El nombre no se puede modificar.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="editar-lote-uso">Destino *</Label>
            <Input
              id="editar-lote-uso"
              type="select"
              value={formEdicion.usoActual}
              onChange={e =>
                setFormEdicion({ ...formEdicion, usoActual: e.target.value })
              }
              disabled={guardandoEdicion}
            >
              {/* si el destino actual ya no está activo en el catálogo se muestra igual */}
              {loteEnEdicion?.usoActual &&
                !USOS_LOTE.some(u => u.value === loteEnEdicion.usoActual) && (
                  <option value={loteEnEdicion.usoActual}>
                    {loteEnEdicion.usoActual}
                  </option>
                )}
              {USOS_LOTE.map(uso => (
                <option key={uso.value} value={uso.value}>
                  {uso.label}
                </option>
              ))}
            </Input>
            {errorUsos && (
              <div className="text-danger small">
                No se pudieron cargar los destinos: {errorUsos}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="editar-lote-tenencia">Tenencia *</Label>
            <Input
              id="editar-lote-tenencia"
              type="select"
              value={formEdicion.tipoTenencia}
              onChange={e =>
                setFormEdicion({
                  ...formEdicion,
                  tipoTenencia: e.target.value,
                })
              }
              disabled={guardandoEdicion}
            >
              {/* un lote sin dato de tenencia (anterior a BDSJEA-463 o de 0 ha) se puede dejar así */}
              {!loteEnEdicion?.tipoTenencia && (
                <option value="">Sin dato</option>
              )}
              {TENENCIAS_LOTE.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              Cambiar la superficie de un lote propio no modifica la de un lote
              arrendado: son lotes distintos.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="editar-lote-superficie">Superficie (hectáreas) *</Label>
            <Input
              id="editar-lote-superficie"
              type="number"
              step="0.01"
              min="0.01"
              value={formEdicion.superficieHa}
              onChange={e =>
                setFormEdicion({
                  ...formEdicion,
                  superficieHa: e.target.value,
                })
              }
              disabled={guardandoEdicion}
            />
            {Number(loteEnEdicion?.superficieHa) === 0 && (
              <small className="text-muted">
                Este lote tiene 0 ha: puede cambiar solo el destino sin cargar
                la superficie.
              </small>
            )}
          </FormGroup>

          <div className="d-flex justify-content-end gap-2">
            <Button
              color="secondary"
              title="Cancelar y cerrar"
              onClick={cerrarEdicion}
              disabled={guardandoEdicion}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              title="Guardar los cambios del lote"
              onClick={handleGuardarEdicion}
              disabled={!edicionValida() || guardandoEdicion}
            >
              {guardandoEdicion ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="mdi mdi-content-save me-1" />
              )}
              Guardar cambios
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default EditorPoligonos
